import { useConfig } from "@/react/story/providers/ConfigContext";
import { useStory } from "@/react/story/providers/StoryContext";
import { useQuery } from "@tanstack/react-query";

type PageStats = {
    views: number;
    avgDuration: number | null;
    linkClicks: number;
}

export type EditorStats = Record<number, PageStats>;

export default function useEditorStats() {
    const Config = useConfig();
    const Story = useStory();

    const companyIri = Story.story?.company;

    // Même extraction que dans `usePageStats` : par filtrage, pas par `parseInt` sur l'IRI
    // entier. Sur un IRI qui ne finit pas par un nombre, `replace` rend l'IRI inchangé et
    // `parseInt` rend `NaN` — que le `enabled` ci-dessous laissait passer, faute de tester
    // autre chose que `null`. La requête partait alors sur `/company/NaN/story/…`, dont le
    // `{companyId}` est contraint à `\d+` : 404, `throw`, et tentatives de reprise.
    const companyMatch = companyIri?.match(/\/(\d+)$/) ?? null;
    const companyId = companyMatch ? parseInt(companyMatch[1], 10) : null;
    const storyId = Story.story?.id;

    const Query = useQuery<EditorStats>({
        queryKey: [ "story-page-stats", storyId ],
        queryFn: async () => {
            const response = await fetch(`/company/${companyId}/story/${storyId}/editor/stats`);
            if (!response.ok) {
                throw new Error(response.statusText);
            }

            return response.json();
        },
        // `storyId` vient de `Story.story?.id` : il vaut `undefined`, jamais `null`, quand la
        // story manque. Un `null !== storyId` était donc toujours vrai et ne gardait rien.
        enabled: !Config.isOnline && null !== companyId && undefined !== storyId,
        staleTime: 30_000,
        refetchOnWindowFocus: true,
    });

    return Query.data ?? {};
}
